import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/cn";

export const STEPS = [1, 2, 3, 4, 5, 6] as const;

interface StepRailProps {
  current: number;
  /** Highest unlocked step; anything beyond is disabled. */
  reached: number;
  onSelect: (step: number) => void;
}

export function StepRail({ current, reached, onSelect }: StepRailProps) {
  const { t } = useI18n();

  return (
    // Below lg this is a horizontal strip of chips, as the prototype intended:
    // a 236px vertical column put ~370px of secondary navigation above the form
    // on every mobile page load.
    <nav
      className={cn(
        // The negative margin bleeds the scroll strip to the screen edges, so
        // it must track .page-shell's gutter at each breakpoint.
        "-mx-4 flex w-auto shrink-0 gap-2 overflow-x-auto px-4 pb-1 sm:-mx-5.5 sm:px-5.5",
        "lg:sticky lg:top-[86px] lg:mx-0 lg:w-[236px] lg:flex-col lg:gap-0 lg:overflow-visible lg:px-0 lg:pb-0",
      )}
      aria-label={t("rail.aria")}
    >
      <h4 className="mb-3 hidden font-sans text-[11px] font-semibold tracking-[0.11em] text-muted uppercase lg:block">
        {t("rail.heading")}
      </h4>

      {STEPS.map((step, index) => {
        const locked = index > reached;
        const active = index === current;
        // "Done" means passed, not merely unlocked - the current step is not done.
        const done = index < current && !locked;

        return (
          <button
            key={step}
            type="button"
            disabled={locked}
            aria-current={active ? "step" : undefined}
            onClick={() => onSelect(index)}
            className={cn(
              "flex min-h-11 shrink-0 items-center gap-3 rounded-[9px] border-0 px-3 py-2.5 text-left whitespace-nowrap transition-colors",
              "lg:mb-0.5 lg:w-full lg:items-start lg:whitespace-normal",
              locked && "cursor-not-allowed opacity-45",
              active ? "bg-surface text-ink shadow-card" : "bg-transparent text-muted",
              !active && !locked && "hover:bg-[#e9eff7]",
            )}
          >
            <span
              className={cn(
                "grid size-[23px] shrink-0 place-items-center rounded-full border-[1.5px] font-mono text-[11.5px] font-semibold transition-colors",
                done && "border-ok bg-ok text-white",
                active && "border-navy bg-navy text-white",
                !done && !active && "border-line bg-surface",
              )}
            >
              {done ? "✓" : index + 1}
            </span>
            <span className="pt-px text-[13.5px] font-medium">
              {/* Element, not bare text, for the same translation-safety reason
                  as elsewhere: the sibling below would break reconciliation. */}
              <span>{t(`rail.step${step}.title`)}</span>
              {/* Subtitle is desktop-only, as in the prototype: on a chip strip
                  it doubles the height and adds nothing. */}
              <span className="mt-px hidden text-[11.5px] font-normal text-muted lg:block">
                {t(`rail.step${step}.subtitle`)}
              </span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}
